"use client";

import { useEffect, useRef, useState } from "react";
import Icon from "@/components/ui/AppIcon";

interface FormState {
  name: string;
  company: string;
  email: string;
  service: string;
  message: string;
}

interface FieldErrors {
  name?: string;
  email?: string;
  service?: string;
  message?: string;
}

function Toast({ message, onClose }: { message: string; onClose: () => void }) {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className="fixed top-6 right-6 z-50 flex items-center gap-3 bg-emerald-900/90 border border-emerald-500/40 text-emerald-300 text-sm px-5 py-3.5 rounded-lg shadow-xl backdrop-blur-md">
      <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
      <span>{message}</span>
      <button onClick={onClose} className="ml-2 text-emerald-400/60 hover:text-emerald-300 transition-colors">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
      {message}
    </p>
  );
}

export default function ContactSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [form, setForm] = useState<FormState>({ name: "", company: "", email: "", service: "", message: "" });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    const el = sectionRef.current;
    if (el) el.querySelectorAll(".reveal").forEach((i) => observer.observe(i));
    return () => observer.disconnect();
  }, []);

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case "name":
        if (!value.trim()) return "Full name is required.";
        if (value.trim().length < 2) return "Name must be at least 2 characters.";
        return "";
      case "email":
        if (!value.trim()) return "Email address is required.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Please enter a valid email address.";
        return "";
      case "service":
        if (!value) return "Please select a service of interest.";
        return "";
      case "message":
        if (!value.trim()) return "Please describe your operational challenge.";
        if (value.trim().length < 10) return "Message must be at least 10 characters.";
        return "";
      default:
        return "";
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { id, value } = e.target;
    setForm((f) => ({ ...f, [id]: value }));
    if (touched[id]) {
      setFieldErrors((prev) => ({ ...prev, [id]: validateField(id, value) }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { id, value } = e.target;
    setTouched((prev) => ({ ...prev, [id]: true }));
    setFieldErrors((prev) => ({ ...prev, [id]: validateField(id, value) }));
  };

  const inputClass = (field: keyof FieldErrors) =>
    `w-full bg-[rgba(255,255,255,0.04)] border rounded-lg px-4 py-3 text-[14px] font-body text-cream placeholder-muted/50 focus:outline-none transition-colors ${
      touched[field] && fieldErrors[field]
        ? "border-red-500/60 focus:border-red-500" :"border-[rgba(184,134,11,0.35)] focus:border-gold/70"
    }`;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const allTouched = { name: true, email: true, service: true, message: true };
    setTouched(allTouched);
    const errors: FieldErrors = {
      name: validateField("name", form.name),
      email: validateField("email", form.email),
      service: validateField("service", form.service),
      message: validateField("message", form.message),
    };
    setFieldErrors(errors);
    if (Object.values(errors).some((e) => e)) return;

    setToast("Your message has been sent! A StanDave consultant will contact you within 24 hours.");
    setSubmitted(true);
  };

  return (
    <section id="contact" ref={sectionRef} className="relative py-28 bg-[#0A1628]">
      {toast && <Toast message={toast} onClose={() => setToast("")} />}
      <div className="absolute top-0 left-0 right-0 gold-line" />

      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          {/* Left */}
          <div className="lg:col-span-5 space-y-8">
            <div className="reveal">
              <p className="text-[11px] font-display uppercase tracking-[0.2em] text-gold mb-3">
                Get In Touch
              </p>
              <h2 className="text-4xl md:text-5xl font-display font-700 text-cream leading-tight tracking-tight">
                Let&apos;s Optimize Your Business Today
              </h2>
            </div>
            <p className="reveal delay-100 text-[18px] font-body font-500 text-cream/80 leading-[1.75]">
              Whether you need streamlined logistics, better inventory control, or strategic business guidance, StanDave LLC is ready to help.
            </p>

            <div className="reveal delay-200 space-y-5">
              {[
                { icon: "EnvelopeIcon", label: "Email Us", value: "support@standavellc.com" },
                { icon: "MapPinIcon", label: "Headquarters", value: "Atlanta, Georgia, USA" },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[rgba(184,134,11,0.15)] border border-[rgba(184,134,11,0.3)] flex items-center justify-center flex-shrink-0">
                    <Icon name={item.icon as "EnvelopeIcon"} size={17} className="text-gold" />
                  </div>
                  <div>
                    <div className="text-[11px] font-display uppercase tracking-widest text-muted">
                      {item.label}
                    </div>
                    <div className="text-[16px] font-body font-500 text-cream/90">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-7 reveal delay-200">
            {submitted ? (
              <div className="service-card rounded-2xl p-12 text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-[rgba(52,211,153,0.1)] flex items-center justify-center mx-auto">
                  <Icon name="CheckCircleIcon" size={32} className="text-emerald-400" />
                </div>
                <h3 className="text-2xl font-display font-700 text-cream">Message Received</h3>
                <p className="text-[17px] font-body font-500 text-cream/75">
                  Thank you for reaching out. A StanDave consultant will contact you within 24 business hours.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="service-card rounded-2xl p-8 md:p-10 space-y-6"
                noValidate
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Full Name */}
                  <div className="space-y-1">
                    <label
                      htmlFor="name"
                      className="text-[11px] font-display uppercase tracking-widest text-muted"
                    >
                      Full Name <span className="text-gold">*</span>
                    </label>
                    <input
                      id="name"
                      type="text"
                      placeholder="John Carter"
                      value={form.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={inputClass("name")}
                    />
                    <FieldError message={touched.name ? fieldErrors.name : undefined} />
                  </div>

                  {/* Company */}
                  <div className="space-y-1">
                    <label
                      htmlFor="company"
                      className="text-[11px] font-display uppercase tracking-widest text-muted"
                    >
                      Company
                    </label>
                    <input
                      id="company"
                      type="text"
                      placeholder="Acme Corp"
                      value={form.company}
                      onChange={handleChange}
                      className="w-full bg-[rgba(255,255,255,0.04)] border border-[rgba(184,134,11,0.3)] rounded-lg px-4 py-3 text-[14px] font-body text-cream placeholder-muted/50 focus:outline-none focus:border-gold/70 transition-colors"
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-1">
                  <label
                    htmlFor="email"
                    className="text-[11px] font-display uppercase tracking-widest text-muted"
                  >
                    Email Address <span className="text-gold">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    placeholder="john@company.com"
                    value={form.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={inputClass("email")}
                  />
                  <FieldError message={touched.email ? fieldErrors.email : undefined} />
                </div>

                {/* Service */}
                <div className="space-y-1">
                  <label
                    htmlFor="service"
                    className="text-[11px] font-display uppercase tracking-widest text-muted"
                  >
                    Service of Interest <span className="text-gold">*</span>
                  </label>
                  <select
                    id="service"
                    value={form.service}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full bg-[rgba(12,27,51,0.9)] border rounded-lg px-4 py-3 text-[14px] font-body text-cream/80 focus:outline-none transition-colors ${
                      touched.service && fieldErrors.service
                        ? "border-red-500/60 focus:border-red-500" :"border-[rgba(184,134,11,0.35)] focus:border-gold/70"
                    }`}
                  >
                    <option value="">Select a service...</option>
                    <option value="asset">Asset Management</option>
                    <option value="inventory">Inventory Management</option>
                    <option value="logistics">Logistics &amp; Transportation</option>
                    <option value="consulting">Business Consulting &amp; Financial Planning</option>
                    <option value="transactions">Global Transaction &amp; Funding Solutions</option>
                  </select>
                  <FieldError message={touched.service ? fieldErrors.service : undefined} />
                </div>

                {/* Message */}
                <div className="space-y-1">
                  <label
                    htmlFor="message"
                    className="text-[11px] font-display uppercase tracking-widest text-muted"
                  >
                    Message <span className="text-gold">*</span>
                  </label>
                  <textarea
                    id="message"
                    rows={4}
                    placeholder="Briefly describe your operational challenge..."
                    value={form.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full bg-[rgba(255,255,255,0.04)] border rounded-lg px-4 py-3 text-[14px] font-body text-cream placeholder-muted/50 focus:outline-none transition-colors resize-none ${
                      touched.message && fieldErrors.message
                        ? "border-red-500/60 focus:border-red-500" :"border-[rgba(184,134,11,0.35)] focus:border-gold/70"
                    }`}
                  />
                  <FieldError message={touched.message ? fieldErrors.message : undefined} />
                </div>

                <button
                  type="submit"
                  className="btn-primary w-full py-4 text-sm rounded-lg font-display flex items-center justify-center gap-2"
                >
                  Send Message
                  <Icon name="ArrowRightIcon" size={16} className="text-navy" />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}